import {
    getNavigatorLastRecentViewsList,
    VIEWS_KEY,
} from '@eventbrite/personalization';
import isEmpty from 'lodash/isEmpty';
import type { bboxShape, Event, LocationData } from './types';
import { ChildEventProps, SALES_STATUS } from './types';

export const eventIsOnSale = (event: ChildEventProps) =>
    (event?.is_sales_ended === false && event?.is_sold_out === false) ||
    (event &&
        'sales_status' in event &&
        event?.sales_status === SALES_STATUS.ON_SALE);

export const eventHasUnknownSalesStatus = (event: ChildEventProps) =>
    event &&
    'sales_status' in event &&
    event?.sales_status === SALES_STATUS.DEFAULT;

export const eventIsSalesEnded = (event: ChildEventProps) =>
    (event && 'is_sales_ended' in event && event?.is_sales_ended == true) ||
    (event &&
        'sales_status' in event &&
        event?.sales_status === SALES_STATUS.SALES_ENDED);

export const eventIsSoldOut = (event: ChildEventProps) =>
    (event && 'is_sold_out' in event && event?.is_sold_out == true) ||
    (event &&
        'sales_status' in event &&
        event?.sales_status === SALES_STATUS.SOLD_OUT);

export const getSalesStatus = (event: ChildEventProps) => {
    if (event?.sales_status) {
        return event.sales_status;
    } else {
        return eventIsOnSale(event)
            ? SALES_STATUS.ON_SALE
            : SALES_STATUS.DEFAULT;
    }
};

export const getPurchasableEvents = (childEvents: ChildEventProps[] | null) => {
    return childEvents?.filter(eventIsOnSale);
};

export const findFirstPurchasableEvent = (
    childEvents: ChildEventProps[] | null,
) => {
    if (!childEvents) return null;
    const availableEvents: ChildEventProps[] =
        childEvents.filter(eventIsOnSale);
    return availableEvents[0] ?? null;
};

export const getEventChildById = (
    childEvents: ChildEventProps[] | null,
    eventId: string,
) => childEvents?.find((event) => event.id === eventId) || {};

export const isYTShortVerticalVideo = (url?: string) => {
    return url && isYoutubeURL(url) && url.includes('short');
};

export const isYTHorizontalVideoURL = (url: string) => {
    return isYoutubeURL(url) && !url.includes('short');
};

const isYoutubeURL = (url: string) => {
    const pattern = /^(http(s)?:\/\/)?((w){3}.)?youtu(be|.be)?(\.com)?\/.+/;
    return pattern.test(url);
};

export const buildSearchLocation = (location: LocationData = {}) => {
    const { placeId, bbox, isOnline, longitude, latitude } = location;
    if (isEmpty(bbox) && longitude && latitude) {
        location.bbox = buildBBox({ latitude, longitude });
    }
    const searchOptions = {
        places: placeId ? [placeId] : undefined,
        online_events_only: isOnline ? isOnline : undefined,
        bbox: location?.bbox ? concatLocationBBox(location?.bbox) : undefined,
    };
    return searchOptions;
};

export const buildBBox = ({
    latitude,
    longitude,
}: {
    latitude: number;
    longitude: number;
}) => ({
    sw: { lng: longitude - 0.4, lat: latitude - 0.3 },
    ne: { lng: longitude + 0.4, lat: latitude + 0.3 },
});

export const concatLocationBBox = (bbox: bboxShape) =>
    `${bbox.sw?.lng},${bbox.sw?.lat},${bbox.ne?.lng},${bbox.ne?.lat}`;

export const updateRecentViews = (event: Event) => {
    const eventsToStore: string[] = [
        event.id,
        ...getNavigatorLastRecentViewsList(9),
    ];
    const dedupedEventsToStore = [...new Set(eventsToStore)];
    localStorage.setItem(VIEWS_KEY, dedupedEventsToStore.toString());
};
